import { useEffect, useRef } from 'react';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { createScaffolderFieldExtension } from '@backstage/plugin-scaffolder-react';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { useCurrentUserEmail } from '../components/catalog/microapps/hooks';

/**
 * Hidden field extension that auto-populates `ownerEmails` with the
 * current user's email on a fresh deploy.
 *
 * On redeploy the field is pre-filled from the backstage.yaml annotation
 * (`formData` already contains the existing owner list), so we leave it.
 * On a fresh deploy `formData` is empty, so we seed it with the deployer's
 * email so it is captured in the `microapp/owners` annotation.
 *
 * Usage in template YAML:
 *   ownerEmails:
 *     type: array
 *     items:
 *       type: string
 *     ui:field: OwnerEmailsField
 *     ui:backstage:
 *       review:
 *         show: false
 */
export const OwnerEmailsField = ({
  onChange,
  formData,
}: FieldExtensionComponentProps<string[]>) => {
  const currentUserEmail = useCurrentUserEmail();
  const initializedRef = useRef(false);
  const formDataRef = useRef(formData);
  formDataRef.current = formData;

  useEffect(() => {
    if (!currentUserEmail || initializedRef.current) return;
    initializedRef.current = true;
    // Only seed when field is empty (fresh deploy). On redeploy it already has values.
    if (!formDataRef.current || formDataRef.current.length === 0) {
      onChange([currentUserEmail.toLowerCase()]);
    }
  }, [currentUserEmail, onChange]);

  // Render nothing — this is a hidden data field.
  return null;
};

export const OwnerEmailsFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'OwnerEmailsField',
    component: OwnerEmailsField,
  }),
);
